/**
 * GlobalFilterBar - Displays active global filters below the header
 *
 * Shows filter chips for active account, environment, and time range filters.
 * Only renders when at least one global filter is active.
 *
 * `timeOnly` restricts the bar to the time-range filter for routes that support
 * only global time filtering and not account/environment (e.g. the AI
 * Applications catalog): account/env chips are hidden even when set elsewhere
 * in the shared global state, the bar renders only when a time filter is
 * active, and "Clear All" clears just the time filter (leaving any account/env
 * selection intact for the pages that do use it).
 */

import React from 'react'
import { Close, FilterList } from '@mui/icons-material'

import {
  type GlobalFilterState,
  useGlobalFilter,
} from '../contexts/GlobalFilterContext'
import { resolvedTimeFilterLabel } from '../utils/filterHelpers'

import FilterChip from './FilterChip'

type GlobalFilterBarProps = {
  className?: string
  timeOnly?: boolean
}

// @purpose Header sub-bar rendering removable chips for the active global account, environment, and time-range filters
const GlobalFilterBar = ({
  className = '',
  timeOnly = false,
}: GlobalFilterBarProps): React.JSX.Element | null => {
  const {
    filters,
    hasActiveFilters,
    hasActiveTimeFilter,
    updateFilter,
    clearAllFilters,
    clearTimeFilter,
  } = useGlobalFilter()

  // Don't render if no relevant filter is active. In timeOnly mode only the
  // time filter counts toward visibility (account/env are not shown here).
  const hasVisibleFilter = timeOnly
    ? hasActiveTimeFilter
    : hasActiveFilters || hasActiveTimeFilter
  if (!hasVisibleFilter) {
    return null
  }

  const getAccountDisplayValue = (accountIds: string[]): string => {
    if (accountIds.length === 0) return ''
    if (accountIds.length === 1) return accountIds[0]
    return `${accountIds.length} accounts`
  }

  const getEnvironmentDisplayValue = (environments: string[]): string => {
    if (environments.length === 0) return ''
    if (environments.length === 1) return environments[0]
    return `${environments.length} environments`
  }

  const handleRemoveFilter = (key: keyof GlobalFilterState) => {
    updateFilter(
      key as keyof Pick<GlobalFilterState, 'accountId' | 'environment'>,
      [],
    )
  }

  return (
    <div
      className={`bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 px-6 py-2 ${className}`}
      data-testid="global-filter-bar"
      data-mode={timeOnly ? 'time' : 'full'}
    >
      <div className="flex items-center gap-3">
        {/* Filter Icon */}
        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
          <FilterList sx={{ fontSize: 18 }} />
          <span className="text-xs font-medium uppercase tracking-wide">
            Global Filters
          </span>
        </div>

        {/* Divider */}
        <div className="h-4 w-px bg-slate-300 dark:bg-slate-600" />

        {/* Filter Chips */}
        <div className="flex items-center gap-2 flex-wrap">
          {!timeOnly && filters.accountId.length > 0 && (
            <FilterChip
              filterLabel="Account"
              displayValue={getAccountDisplayValue(filters.accountId)}
              onRemove={() => handleRemoveFilter('accountId')}
            />
          )}

          {!timeOnly && filters.environment.length > 0 && (
            <FilterChip
              filterLabel="Environment"
              displayValue={getEnvironmentDisplayValue(filters.environment)}
              onRemove={() => handleRemoveFilter('environment')}
            />
          )}

          {hasActiveTimeFilter && (
            <FilterChip
              filterLabel={`${
                filters.timestampMode === 'first_seen'
                  ? 'Earliest activity'
                  : 'Most recent activity'
              } (${filters.timezoneMode === 'utc' ? 'UTC' : 'Local'})`}
              displayValue={resolvedTimeFilterLabel(
                filters.timeFilter,
                filters.timezoneMode,
              )}
              onRemove={clearTimeFilter}
            />
          )}
        </div>

        {/* Clear All Button. In timeOnly mode this clears only the time filter
            so an account/env selection made on another page is left intact. */}
        <button
          onClick={timeOnly ? clearTimeFilter : clearAllFilters}
          className="ml-auto flex items-center gap-1 px-2 py-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 rounded transition-colors"
          aria-label="Clear all global filters"
        >
          <Close sx={{ fontSize: 14 }} />
          <span>Clear All</span>
        </button>
      </div>
    </div>
  )
}

export default GlobalFilterBar
